import React, { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Plus, FolderOpen, AlertTriangle } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Spinner } from "../components/ui/shadcn-io/spinner";
import { createProjectColumns, Project } from "../lib/projectTableColumns";
import { SimpleDataTable } from "../components/simpleDataTable";

export default function Projects() {
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      window.history.replaceState(null, "");

      setTimeout(() => {
        setSuccessMessage("");
      }, 5000);
    }
  }, [location.state]);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setIsLoading(true);
      setError("");

      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/projects`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Map backend data to frontend Project type
      const mappedProjects: Project[] = response.data.data.map(
        (project: any) => ({
          id: project._id,
          projectName: project.projectName,
          clientId: {
            _id: project.clientId._id,
            clientName: project.clientId.clientName,
            contactPerson: {
              name: project.clientId.contactPerson?.name || "N/A",
              email: project.clientId.contactPerson?.email || "N/A",
            },
          },
          status: project.status,
          priority: project.priority,
          timeline: {
            startDate: project.timeline.startDate,
            estimatedEndDate: project.timeline.estimatedEndDate,
            actualEndDate: project.timeline.actualEndDate,
          },
          paymentStructure: {
            totalAmount: project.paymentStructure.totalAmount,
            totalPaid: project.paymentStructure.totalPaid,
            remainingAmount: project.paymentStructure.remainingAmount,
            currency: project.paymentStructure.currency || "INR",
          },
          progress: project.progress,
          hasInactiveEmployees: project.hasInactiveEmployees || false,
          inactiveEmployeesCount: project.inactiveEmployeesCount || 0,
          createdAt: project.createdAt,
        })
      );

      setProjects(mappedProjects);
    } catch (error: any) {
      console.error("Error fetching projects:", error);
      setError(error.response?.data?.message || "Failed to fetch projects");
    } finally {
      setIsLoading(false);
    }
  };

  const handleProjectDeleted = () => {
    fetchProjects();
    setSuccessMessage("Project deleted successfully!");
  };

  const columns = createProjectColumns(handleProjectDeleted);

  // Calculate stats
  const stats = {
    total: projects.length,
    active: projects.filter((p) => p.status === "in_progress").length,
    withInactiveEmployees: projects.filter((p) => p.hasInactiveEmployees)
      .length,
    completed: projects.filter((p) => p.status === "completed").length,
  };

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Projects</h1>
            <p className="text-muted-foreground">
              Manage all your client projects
            </p>
          </div>
        </div>
        <div className="flex items-center justify-center h-[400px]">
          <div className="text-center space-y-4">
            <Spinner variant="ellipsis" size={40} className="text-primary" />
            <div>
              <h3 className="text-lg font-medium">Loading projects...</h3>
              <p className="text-sm text-muted-foreground">
                Please wait while we fetch the data
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight">Projects</h1>
          <p className="text-muted-foreground">
            Manage all your client projects
          </p>
        </div>
        <Button onClick={() => navigate("/projects/add")}>
          <Plus className="mr-2 h-4 w-4" /> Add Project
        </Button>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4">
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 rounded-full bg-green-500" />
            <p className="text-sm font-medium text-green-800">Success</p>
          </div>
          <p className="mt-1 text-sm text-green-700">{successMessage}</p>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 rounded-full bg-red-500" />
            <p className="text-sm font-medium text-red-800">Error</p>
          </div>
          <p className="mt-1 text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Warning for projects with inactive employees */}
      {stats.withInactiveEmployees > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <p className="text-sm font-medium text-amber-800">
              Action Required
            </p>
          </div>
          <p className="mt-1 text-sm text-amber-700">
            {stats.withInactiveEmployees} project(s) have inactive employees.
            Click on a project to view details and manage team members.
          </p>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Total Projects
              </p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
            <FolderOpen className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Active Projects
              </p>
              <p className="text-2xl font-bold">{stats.active}</p>
            </div>
            <FolderOpen className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                With Inactive Staff
              </p>
              <p className="text-2xl font-bold">
                {stats.withInactiveEmployees}
              </p>
            </div>
            <AlertTriangle className="h-8 w-8 text-amber-600" />
          </div>
        </div>

        <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Completed
              </p>
              <p className="text-2xl font-bold">{stats.completed}</p>
            </div>
            <FolderOpen className="h-8 w-8 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Data Table */}
      <SimpleDataTable columns={columns} data={projects} />
    </div>
  );
}
